import { Check, Ellipsis, Pencil, Trash2, X, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { Item, ItemContent } from "@repo/ui/components/ui/item";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import {
  historyEntryIconBtn,
  historyEntryMenuBtn,
  historyEntryRowStyles as s,
  historyEntryTimestamp,
} from "./HistoryEntryRow.styles";
import type { HistoryEntryRowProps } from "./HistoryEntryRow.types";

const UNTITLED_TITLE = "Untitled conversation";

type RowMode = "idle" | "rename" | "confirmDelete";

function ErrorNote({ message }: { message: string }) {
  return (
    <p role="alert" className={s.error}>
      {message}
    </p>
  );
}

function RowIconButton({
  icon: Icon,
  tone,
  label,
  tooltip,
  onClick,
  disabled = false,
  autoFocus = false,
}: {
  icon: LucideIcon;
  tone?: "neutral" | "primary" | "danger";
  label: string;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={tooltip}
      autoFocus={autoFocus}
      className={historyEntryIconBtn({ tone })}
    >
      <Icon className={s.icon} />
    </button>
  );
}

/**
 * One conversation in the history list, with rename and delete folded into the row rather
 * than a modal. Writes go through the parent and only commit once the server accepts.
 */
export function HistoryEntryRow({
  entry,
  timestamp,
  onSelect,
  onRename,
  onDelete,
}: HistoryEntryRowProps) {
  const [mode, setMode] = useState<RowMode>("idle");
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const title = entry.title?.trim() || UNTITLED_TITLE;
  const draftTitle = draft.trim();

  const startRename = () => {
    // Seed from the raw title, not the placeholder — an untitled conversation opens empty.
    setDraft(entry.title ?? "");
    setError(undefined);
    setMode("rename");
  };

  const startDelete = () => {
    setError(undefined);
    setMode("confirmDelete");
  };

  const cancel = () => {
    setError(undefined);
    setMode("idle");
  };

  const run = async (
    action: () => void | Promise<void>,
    failure: string,
    onSuccess?: () => void,
  ) => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await action();
      onSuccess?.();
    } catch {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  const submitRename = () => {
    if (busy) return;
    if (draftTitle.length === 0) return;
    if (draftTitle === entry.title) {
      setMode("idle");
      return;
    }
    void run(
      () => onRename?.(draftTitle),
      "Couldn't rename this conversation.",
      () => setMode("idle"),
    );
  };

  const confirmDelete = () => {
    // On success the parent drops the entry and the row unmounts; nothing to reset.
    void run(() => onDelete?.(), "Couldn't delete this conversation.");
  };

  if (mode === "rename") {
    return (
      <Item size="sm" className={s.renameRow}>
        <input
          autoFocus
          value={draft}
          disabled={busy}
          aria-label="Conversation title"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submitRename();
            if (event.key === "Escape") cancel();
          }}
          className={s.input}
        />
        <RowIconButton
          icon={Check}
          tone="primary"
          label="Save title"
          tooltip="Save"
          onClick={submitRename}
          disabled={busy || draftTitle.length === 0}
        />
        <RowIconButton
          icon={X}
          label="Cancel rename"
          tooltip="Cancel"
          onClick={cancel}
          disabled={busy}
        />
        {error && <ErrorNote message={error} />}
      </Item>
    );
  }

  if (mode === "confirmDelete") {
    return (
      <Item size="sm" className={s.deleteRow}>
        <p className={s.confirmLabel}>Permanently delete?</p>
        <RowIconButton
          icon={Trash2}
          tone="danger"
          label={`Permanently delete ${title}`}
          tooltip="Delete permanently"
          onClick={confirmDelete}
          disabled={busy}
        />
        <RowIconButton
          icon={X}
          label="Cancel delete"
          tooltip="Cancel"
          onClick={cancel}
          disabled={busy}
          // Focus parks on the safe choice, so a stray Enter cannot confirm the delete.
          autoFocus
        />
        {error && <ErrorNote message={error} />}
      </Item>
    );
  }

  const canRename = Boolean(onRename);
  const canDelete = Boolean(onDelete);

  return (
    <Item size="sm" className={s.row}>
      <button type="button" onClick={onSelect} className={s.selectBtn}>
        <ItemContent className={s.entryContent}>
          <p className={s.entryText}>{title}</p>
        </ItemContent>
      </button>
      <div className={s.trailing}>
        {timestamp && (
          <span className={historyEntryTimestamp({ concealed: menuOpen })}>{timestamp}</span>
        )}
        {(canRename || canDelete) && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger
              aria-label={`Conversation options for ${title}`}
              title="Options"
              className={historyEntryMenuBtn({ revealed: menuOpen })}
            >
              <Ellipsis className={s.icon} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={2} className={s.menuContent}>
              {canRename && (
                <DropdownMenuItem className={s.menuItem} onSelect={startRename}>
                  <Pencil />
                  Rename
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  variant="destructive"
                  className={s.menuItem}
                  onSelect={startDelete}
                >
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </Item>
  );
}
